import { gql } from '@apollo/client';

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Get or create a support chat room for the current user
 * No input needed — backend derives user from auth token
 */
export const getOrCreateSupportChatRoomV2 = gql`
  mutation GetOrCreateSupportChatRoomV2 {
    getOrCreateSupportChatRoom {
      id
      chatType
      hostID
      guestID
      participantIDs
      lastMessageContent
      lastMessageAt
      lastMessageType
      lastMessageIsSystemMessage
      unreadCounts {
        userID
        count
      }
      host {
        id
        firstName
        lastName
        profilePicture
      }
      guest {
        id
        firstName
        lastName
        profilePicture
      }
      isNew
      createdAt
    }
  }
`;

/**
 * Get or create a chat room for a studio
 * Returns existing chat room if one exists, otherwise creates a new one
 */
export const getOrCreateChatRoomV2 = gql`
  mutation GetOrCreateChatRoomV2($input: GetOrCreateChatRoomInput!) {
    getOrCreateChatRoom(input: $input) {
      id
      chatType
      participantIDs
      studioID
      hostID
      guestID
      lastMessageContent
      lastMessageAt
      lastMessageSenderID
      lastMessageType
      lastMessageIsSystemMessage
      studio {
        id
        StudioName
        image
        images
        city
        pricePerHour
      }
      host {
        id
        firstName
        lastName
        profilePicture
      }
      guest {
        id
        firstName
        lastName
        profilePicture
      }
    }
  }
`;

/**
 * Send a secure message (ONLY TEXT type allowed from client)
 * Other message types (SYSTEM, BOOKING_*) are server-only
 */
export const sendMessageSecureV2 = gql`
  mutation SendMessageSecureV2($input: SendMessageSecureInput!) {
    sendMessageSecure(input: $input) {
      id
      participantIDs
      content
      type
      chatRoomID
      senderID
      senderFirstName
      senderProfilePicture
      senderRole
      isSystemMessage
      relatedBookingID
      isBookingRelated
      threadGroupID
      createdAt
      updatedAt
    }
  }
`;

/**
 * Mark a chat room as read (resets unread count)
 */
export const markChatAsReadV2 = gql`
  mutation MarkChatAsReadV2($input: MarkChatAsReadInput!) {
    markChatAsRead(input: $input) {
      success
      chatRoomID
    }
  }
`;

/**
 * Delete a chat room
 * NOTE: This uses the auto-generated deleteChatRoom mutation from @model
 * Auth rules may block this - if so, a Lambda-backed delete mutation would be needed
 */
export const deleteChatRoomV2 = gql`
  mutation DeleteChatRoomV2($input: DeleteChatRoomInput!) {
    deleteChatRoom(input: $input) {
      id
      participantIDs
      studioID
      hostID
      guestID
      createdAt
    }
  }
`;

// ============================================================================
// QUERIES
// ============================================================================

/**
 * List chat rooms where the current user is the host
 */
export const listChatRoomsByHost = gql`
  query ListChatRoomsByHost($hostID: ID!, $limit: Int, $nextToken: String) {
    chatRoomsByHostID(hostID: $hostID, limit: $limit, nextToken: $nextToken) {
      items {
        id
        chatType
        threadKind
        participantIDs
        studioID
        hostID
        guestID
        guestStudioIndex
        linkedBookingID
        linkedBookingIDs
        bookingStartDate
        bookingEndDate
        isBookingRelated
        unreadCounts {
          userID
          count
        }
        participantCount
        chatStatus
        retentionPolicy
        threadGroupID
        threadGroupName
        archivedByHost
        archivedByHostAt
        archivedByGuest
        archivedByGuestAt
        becameReadOnlyAt
        scheduledForDeletionAt
        lastMessageContent
        lastMessageAt
        lastMessageSenderID
        lastMessageType
        lastMessageIsSystemMessage
        hostUnreadCount
        guestUnreadCount
        isArchived
        createdAt
        updatedAt
        bookings(limit: 2) {
          items {
            id
            startDateTime
            endDateTime
            status
          }
        }
        studio {
          id
          StudioName
          image
          images
          city
          pricePerHour
        }
        host {
          id
          firstName
          lastName
          profilePicture
        }
        guest {
          id
          firstName
          lastName
          profilePicture
        }
      }
      nextToken
    }
  }
`;

/**
 * List chat rooms where the current user is the guest
 */
export const listChatRoomsByGuest = gql`
  query ListChatRoomsByGuest($guestID: ID!, $limit: Int, $nextToken: String) {
    chatRoomsByGuestID(guestID: $guestID, limit: $limit, nextToken: $nextToken) {
      items {
        id
        chatType
        threadKind
        participantIDs
        studioID
        hostID
        guestID
        guestStudioIndex
        linkedBookingID
        linkedBookingIDs
        bookingStartDate
        bookingEndDate
        isBookingRelated
        unreadCounts {
          userID
          count
        }
        participantCount
        chatStatus
        retentionPolicy
        threadGroupID
        threadGroupName
        archivedByHost
        archivedByHostAt
        archivedByGuest
        archivedByGuestAt
        becameReadOnlyAt
        scheduledForDeletionAt
        lastMessageContent
        lastMessageAt
        lastMessageSenderID
        lastMessageType
        lastMessageIsSystemMessage
        hostUnreadCount
        guestUnreadCount
        isArchived
        createdAt
        updatedAt
        bookings(limit: 2) {
          items {
            id
            startDateTime
            endDateTime
            status
          }
        }
        studio {
          id
          StudioName
          image
          images
          city
          pricePerHour
        }
        host {
          id
          firstName
          lastName
          profilePicture
        }
        guest {
          id
          firstName
          lastName
          profilePicture
        }
      }
      nextToken
    }
  }
`;

/**
 * Get a single chat room by ID
 */
export const getChatRoomV2 = gql`
  query GetChatRoomV2($id: ID!) {
    getChatRoom(id: $id) {
      id
      chatType
      threadKind
      participantIDs
      studioID
      hostID
      guestID
      guestStudioIndex
      linkedBookingID
      linkedBookingIDs
      bookingStartDate
      bookingEndDate
      isBookingRelated
      unreadCounts {
        userID
        count
      }
      participantCount
      chatStatus
      retentionPolicy
      threadGroupID
      threadGroupName
      archivedByHost
      archivedByHostAt
      archivedByGuest
      archivedByGuestAt
      becameReadOnlyAt
      scheduledForDeletionAt
      lastMessageContent
      lastMessageAt
      lastMessageSenderID
      lastMessageType
      lastMessageIsSystemMessage
      hostUnreadCount
      guestUnreadCount
      isArchived
      createdAt
      updatedAt
      bookings(limit: 2) {
        items {
          id
          startDateTime
          endDateTime
          status
        }
      }
      studio {
        id
        StudioName
        image
        images
        city
        pricePerHour
      }
      host {
        id
        firstName
        lastName
        profilePicture
      }
      guest {
        id
        firstName
        lastName
        profilePicture
      }
    }
  }
`;

/**
 * List messages for a specific chat room
 */
export const listMessagesByChatRoom = gql`
  query ListMessagesByChatRoom(
    $chatRoomID: ID!
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    messagesByChatRoomIDAndCreatedAt(
      chatRoomID: $chatRoomID
      createdAt: $createdAt
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        participantIDs
        content
        type
        chatRoomID
        senderID
        senderFirstName
        senderProfilePicture
        senderRole
        isSystemMessage
        relatedBookingID
        isBookingRelated
        threadGroupID
        createdAt
        updatedAt
        # Booking field temporarily removed - might not be available in schema
        # Will fetch booking data separately using relatedBookingID if needed
      }
      nextToken
    }
  }
`;

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

/**
 * Real-time subscription for new messages in a chat room
 * This is the ONLY subscription that works for messages!
 */
export const onSendMessageSecure = gql`
  subscription OnSendMessageSecure($chatRoomID: ID) {
    onSendMessageSecure(chatRoomID: $chatRoomID) {
      id
      participantIDs
      content
      type
      chatRoomID
      senderID
      senderFirstName
      senderProfilePicture
      senderRole
      isSystemMessage
      relatedBookingID
      isBookingRelated
      threadGroupID
      createdAt
      updatedAt
      # Booking field temporarily removed - might not be available in subscription schema
      # Will fetch booking data separately using relatedBookingID if needed
    }
  }
`;

/**
 * Alias for onSendMessageSecureV2 (for consistency with backend naming)
 */
export const onSendMessageSecureV2 = onSendMessageSecure;
